// ===============
// == Functions ==
// ===============

// An easy to use fetch function that handles errors gracefully
async function fetchData(url) {
    let json;
    try {
        const response = await fetch(url);
        json = await response.json();
    } catch (error) {
        if (error instanceof SyntaxError) {
          console.log('❌ fetchData syntax error:', error);
          return;
        } else {
          console.log('❌ fetchData error:', error);
          return;
        }
    } finally {
        if (json) {
            return json;
        }
    }
}

var currencyFormat = window.themeVariables.settings.currencyCodeEnabled ? window.themeVariables.settings.moneyWithCurrencyFormat : window.themeVariables.settings.moneyFormat;
import {formatMoney} from "//www.runderwear.co.uk/cdn/shop/t/187/assets/theme.js?v=29847257337613182731698419943";

// Update cart drawer on discount code change
function cartDrawerTotal(discount) {
    const dataCartDrawerTotal = document.querySelector('[data-cart-drawer-total]');

    if (dataCartDrawerTotal && discount.cart.total_price) {
        dataCartDrawerTotal.innerHTML = formatMoney(discount.cart.total_price, currencyFormat);
    }
};
document.body.addEventListener('docapp-discount-applied', cartDrawerTotal);

// ===============
// === Classes ===
// ===============

var GlobalProductUpsell = class extends HTMLElement {
    constructor() {
        super();

        // Bundle settings
        this.inBundle = false;

        // Settings
        this.settings = window.globalProductUpsellSettings;
        this.bundleHierarchy = window.bundleHierarchy;
        if (!this.settings) { return };
        this.bundleData = window.bundleMetaobjectsArray;
        this.acceptedSizeValues = ['XXXS', 'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
        this.acceptedTypeValues = ["accessories", "​​base-layer", "bra", "socks", "underwear", "shorts"];

        // Data passed in from data-attributes
        this.env = this.dataset.env;
        this.upsellEnabled = this.dataset.upsellEnabled == 'true';
        this.hideFromBundles = this.dataset.hideFromBundles == 'true';
        this.baseProductId = Number(this.dataset.baseProductId);
        this.baseProductHandle = this.dataset.baseProductHandle;
        this.baseCollectionId = Number(this.dataset.baseCollectionId);
        this.baseCollectionHandle = this.dataset.baseCollectionHandle;
        this.upsellOverrideHandles = this.dataset.upsellOverrideHandles ? this.dataset.upsellOverrideHandles.split(',') : "";
        this.upsellOverrideIds = this.dataset.upsellOverrideIds ? this.dataset.upsellOverrideIds.split(',') : "";
        
        // Various 'Products Required' settings, all defaulting to the main setting
        this.productsRequired = this.settings.productsRequired || 5;
        this.productsRequiredBundleSetting = false;
        this.productsRequiredProductSetting = this.dataset.productsRequiredOverride || false;

        // Various data states
        // These variables will be updated with data later on, dependent on
        // whether the base product is in a bundle or enabled for upsell.
        this.baseProductData = {};
        this.baseCollectionData = {};
        this.filteredProductsArray = [];
        this.returnedProducts = [];
        this.cartState = {};
        this.currentBundleData = {};
        this.overridesDataArray = [];

        // Manage event listeners
        this.onVariantAddedListener = this.retrieveBaseProductData.bind(this);

        // Init
        if (this.env == 'CartDrawer') {
            document.addEventListener("cart:change", this.onVariantAddedListener);
            document.body.classList.add('upsell-active');
        } else if (this.baseProductId && this.baseProductHandle) {
            this.init();
        }
    }

    async init() {
        console.log(`%c${this.env} upsell instance – Data initialised`, 'background: #B4F47D; color: #2E2E2E');
        console.log('baseProductId:', this.baseProductId);
        console.log('baseProductHandle:', this.baseProductHandle)

        let filtersRequired = true;

        // Retrieve override product data
        this.overridesDataArray = await this.retrieveOverridesData() || [];

        // If a bundle is detected, its respective collection data will already be populated in `baseCollectionHandle`.
        if (this.hideFromBundles) {
            this.inBundle = false;
            console.log('🚧 Bundle functionality is disabled for this product...');
        } else {
            await this.retrieveBundleData();
        }

        if (!this.upsellEnabled && !this.inBundle) {
            console.log('❌ Smart Upsell is disabled and base product cannot be found in a Multi-buy instance.');
            return;
        }

        // Add loading state to cart drawer product recommendations
        if (this.env == 'CartDrawer') {
            this.cartDrawerAnimate(true);
        }

        // Work out which productsRequire value to use – Either general settings, bundle setting or product setting
        if (this.productsRequiredProductSetting) {
            // If a productsRequired override exists from the base product, we override...
            this.productsRequired = this.productsRequiredProductSetting;
        } else if (this.productsRequiredBundleSetting) {
            // Else, if a productsRequired override exists from the bundle data, we override...
            this.productsRequired = this.productsRequiredBundleSetting;
        }
        // Ensure number of products is never over 5.
        if (this.productsRequired > 5) {
            this.productsRequired = 5;
        }

        // Check whether the amount of overrides is greater than the productsRequire value.
        // If it is, we skip filtering and go straight to displaying the products.
        if (this.overridesDataArray.length >= this.productsRequired) {
            console.log('🚧 Number of product overrides defined for this product exceeds or matches the number of required products. Skipping straight to product output... ');
            filtersRequired = false;
        }

        if (filtersRequired) {
            // If a bundle is not detected, we need to fetch the base collection data from `this.baseCollectionHandle`
            // and populate `this.baseCollectionData` witih the results.
            if (!this.inBundle) await this.retrieveUpsellCollectionData();

            // If we don't have a base collection here... something's wrong
            if (JSON.stringify(this.baseCollectionData) == '{}') {
                console.log('❌ Base collection data missing');
                this.cartDrawerAnimate(false);
                return;
            }

            // To-do: Check that base collection has enough products
            await this.buildProductData();

            // If we don't have our base product data here... something's wrong
            if (!this.baseProductData) {
                console.log('❌ Base product data missing');
                this.cartDrawerAnimate(false);
                return;
            }

            // Update cart state
            if (JSON.stringify(this.cartState) == '{}') {
                const cartData = await fetchData('/cart.json');
                if (!cartData) { return };
                this.cartState = cartData;
            }        

            // Time to filter...
            this.finalProducts = this.runFilters(this.overridesDataArray);
            if (!this.finalProducts) {
                console.log('❌ Issue gathering filter results');
                this.cartDrawerAnimate(false);
                return;
            }
        } else {
            this.finalProducts = this.overridesDataArray;
        }

        this.finalProducts.length = this.productsRequired;

        // Products returned via the filter method have less data than the override products
        // who were fetched directly. The `this.retrieveRichData` method ensures all results
        // have the same rich data.
        this.finalProductsRichData = await this.retrieveRichData(this.finalProducts) || false;

        // Sort by lowest or highest, if defined in the settings
        if (this.settings.sortBy == 'lowest' || this.settings.sortBy == 'highest') {
            this.finalProductsRichData.sort((a, b) => {
                // Calculate the priceFrom values of A and B, returning false if A is higher.
                let aPriceFrom = [];
                a.variants.forEach((variant) => { aPriceFrom.push(Math.round(variant.price * 100)) })
                let bPriceFrom = []
                b.variants.forEach((variant) => { bPriceFrom.push(Math.round(variant.price * 100)) })

                // Find the lowest price from the priceFrom arrays above
                aPriceFrom.length > 0 ? aPriceFrom = Math.min(...aPriceFrom) : aPriceFrom = 0;
                bPriceFrom.length > 0 ? bPriceFrom = Math.min(...bPriceFrom) : bPriceFrom = 0;

                // Depending on the setting, we re-order either cheapest or most expensive first
                if (this.settings.sortBy == 'lowest') {
                    return (aPriceFrom > bPriceFrom) ? 1 : -1;
                }

                if (this.settings.sortBy == 'highest') {
                    return (aPriceFrom > bPriceFrom) ? -1 : 1;
                }
            })
        };

        if (this.finalProductsRichData) {
            this.createUpsellElement();
        }
    }

    async retrieveBaseProductData(event) {
        // If the custom element's env is cartDrawer, we run some extra setup to retrieve the product's 
        // data and calculate whether product upsell should run.

        // If the class has been triggered by the cart:opened event,
        // we need to manually retrieve the cart data and check that 
        // there is enough products to retrieve a base product.
        if (event.type == 'cart:opened') {
            const cartData = await fetchData('/cart.json');
            if (!cartData) { return };
            this.cartState = cartData;
        } else {
            this.cartState = event.detail.cart;
        }

        // Check that there's at least 1 product.
        if (this.cartState.items.length == 0) {
            return false;
        }

        // Store the last product's data
        const lastProduct = this.cartState.items[this.cartState.items.length - 1];

        // Store the product variant's size. This is required later on and is only available here.
        if (lastProduct.variant_options[this.settings.sizeVariantOption - 1]) {
            this.baseProductData['size'] = this.formatSizeValue(lastProduct.variant_options[this.settings.sizeVariantOption - 1]);
        }

        // Update base product data using the product last added to the cart.
        this.baseProductId = lastProduct.product_id;
        this.baseProductHandle = lastProduct.handle;

        // If we're unable to identify the last added product, return.
        if (!this.baseProductId || !this.baseProductHandle) {
            console.log('❌ Product added to cart, but no Product ID was able to be identified.');
            this.cartDrawerAnimate(false);
            return;
        }

        // Retrieve our base product's required metafield data
        const url = (
            `/products/${this.baseProductHandle}?` +
            new URLSearchParams({ view: "upsell-metafields" }).toString()
        );
        const productMetafields = await fetchData(url);
        if (!productMetafields) { return };

        // Now we calculate whether base product is upsell enabled
        let upsellEnabled = this.settings.enabled;
        if (productMetafields.enable == 'Enable') {
            upsellEnabled = true;
        } else if (productMetafields.enable == 'Disable') {
            upsellEnabled = false;
        }

        console.log("❓ Product upsell enabled?", upsellEnabled);

        // Check whether the product has its own max products required
        if (productMetafields.maxProducts) {
            this.productsRequiredProductSetting = productMetafields.maxProducts;
        }

        // Update 'Hide from bundles' setting
        this.hideFromBundles = productMetafields.hideFromBundle == true;

        // If the current env doesn't appear in the product's settings, no upsells will runs.
        if (productMetafields.locations.includes(this.env)) {
            console.log(`❌ Product has the current location (${this.env}) disabled in its metafield settings.`);
            return;
        }

        // Update upsell enabled setting
        this.upsellEnabled = upsellEnabled;
        this.upsellOverrideHandles = productMetafields.overrideHandles;
        this.upsellOverrideIds = productMetafields.overrideIds;
        this.init();
    }

    async retrieveBundleData() {
        console.log('🚧 Attempting to find a bundle...');

        // Check for successor bundle first...
        this.inBundle = false;
        let successorBundle = false;
        let successorBundleData = false;

        if (this.bundleData.length == 0) {
            console.log('🚧 No activated bundles.');
            return;
        }

        // Find all current activeDiscounts
        console.log('🚧 Retrieving all currently-applied discounts...');
    
        let url = ('/cart.json');
        let cartTemplateData = await fetchData(url);
        
        this.activeDiscounts = [];
        cartTemplateData.items.forEach((item) => {
            if (item.discounts.length > 0) {
                item.discounts.forEach((discount) => {
                    this.activeDiscounts.push(discount.title)
                })
            }
        })
        this.activeDiscounts = [...new Set(this.activeDiscounts)];

        console.log('🚧 Active discounts:');
        console.log(this.activeDiscounts);

        // Check active discounts against any live bundles, checking whether they have a successor bundle...
        console.log('🚧 Comparing active discounts to live bundles, looking for the existence of a successor bundle...');
        for (let i = 0; i < this.bundleData.length; i++) { // For loop to extend...

            if (this.activeDiscounts.includes(this.bundleData[i].discountName) && this.bundleData[i].successorBundle) {
                
                // Successor bundle for the active discount found...
                successorBundle = this.bundleData[i].successorBundle;
                successorBundleData = false;
                
                console.log('🚧 Found a successor bundle.');

                // Now we retrieve the bundle's data...
                this.bundleData.forEach((bundle) => {
                    if (bundle.handle == successorBundle) {
                        successorBundleData = bundle;
                    }
                })
                if (!successorBundleData) { continue };

                // If the data is retrieved and all is well, we fetch its products
                console.log('🚧 Fetching its product data to check whether base product and overrides are part of the Multi-buy...');
                let url = (
                    `/collections/${successorBundleData.collectionHandle}/products.json?` +
                    new URLSearchParams({ limit: 1000 }).toString()
                );
                let successorBundleCollectionData = await fetchData(url);

                if (successorBundleCollectionData) {
                    // Array of base product and all override products
                    let baseProductAndOverrides = [this.baseProductId].concat(...this.upsellOverrideIds);
                    baseProductAndOverrides = [...new Set(baseProductAndOverrides)];

                    // Check the collection's products for the base product and all overrides
                    let productsInBundle = successorBundleCollectionData.products.filter((item) => {
                        let id = Number(item.id);
                        return baseProductAndOverrides.includes(id);
                    });

                    console.log('🚧 Base product and override products:');
                    console.log(baseProductAndOverrides);

                    console.log('🚧 Base product and override products that appear in the current bundle collection:');
                    console.log(productsInBundle);

                    // If the base product and all of its overrides are found in the bundle collection...
                    if (productsInBundle.length == baseProductAndOverrides.length) {
                        console.log('🚧 All products exist within the bundle collection!');

                        this.inBundle = true;
                        this.currentBundleData = successorBundleData;
                        this.baseCollectionData = successorBundleCollectionData.products;
                        this.productsRequiredBundleSetting = this.currentBundleData.productsToShow;

                        console.log(`✅ Bundle check complete. Bundle product status: true.`);
                        console.log(this.currentBundleData);
                        return this.inBundle;
                    } else {
                        console.log('🚧 Not all products exist within the bundle collection. Continuing to search...');
                    }
                }

            }
        }

        // By this point, if there is no successor bundle to show, it's possible that 
        // there's still an active discount which should prevent certain bundles from displaying 
        //
        // I.e. the last bundle in a hierarchy for bundles. When this happens, the precursor
        // bundles should be hidden.
        this.activeDiscountBundleHandles = [];
        for (let i = 0; i < this.bundleData.length; i++) {
            for (let ii = 0; ii < this.activeDiscounts.length; ii++) {
                if (this.bundleData[i].discountName == this.activeDiscounts[ii]) {
                    this.activeDiscountBundleHandles.push(this.bundleData[i]);
                }
            }
        };

        console.log('🚧 Checking active bundles...');
        if (this.activeDiscountBundleHandles) { console.log(this.activeDiscountBundleHandles) };

        // Below we loop through all active bundles, and check whether they are the last successor
        // within a line of associated bundles. If they are, all precursor bundles will be blacklisted..
        this.blacklistedBundles = [];
        this.activeDiscountBundleHandles.forEach((activeBundle) => {
            console.log(`🚧 Active bundle found – ${activeBundle.discountName}`);
            for (let i = 0; i < this.bundleHierarchy.length; i++) {
                if (this.bundleHierarchy[i].includes(activeBundle.handle)) {
                    this.blacklistedBundles = this.blacklistedBundles.concat(this.bundleHierarchy[i]);
                    break;
                }
            }
        })

        if (this.blacklistedBundles.length > 0) { console.log('🚧 Blacklisting precursor bundles...') };

        // For each Bundle Metaobject, we'll fetch their collection and check for the existence
        // of the base product. Using a for loop here for its break and continue abilities.
        //
        // To-do, add way of prioritising bundles that have successor bundles defined.
        for (let i = 0; i < this.bundleData.length; i++) {
            console.log('🚧 Checking regular bundles...');

            // Ignore blacklisted bundles
            if (this.blacklistedBundles.includes(this.bundleData[i].handle)) {
                continue;
            }

            let url = (
                `/collections/${this.bundleData[i].collectionHandle}/products.json?` +
                new URLSearchParams({ limit: 1000 }).toString()
            );
            let collectionData = await fetchData(url);

            // Array of base product and all override products
            let baseProductAndOverrides = [this.baseProductId].concat(...this.upsellOverrideIds);
            baseProductAndOverrides = [...new Set(baseProductAndOverrides)];

            // Check the collection's products for the base product and all overrides
            let productsInBundle = collectionData.products.filter((item) => {
                let id = Number(item.id);
                return baseProductAndOverrides.includes(id);
            });

            // If the base product and all of its overrides are found in the collection...
            if (productsInBundle.length == baseProductAndOverrides.length) {
                this.currentBundleData = this.bundleData[i];
                this.inBundle = true;
                this.baseCollectionData = collectionData.products;
                this.productsRequiredBundleSetting = this.currentBundleData.productsToShow;
                break;
            }
        }
        console.log(`✅ Bundle check complete. Bundle product status: ${this.inBundle}`);
        if (this.inBundle) { console.log(this.currentBundleData) };
        return this.inBundle;
    }

    async retrieveUpsellCollectionData() {
        if (!this.baseCollectionHandle) { return };

        console.log('🚧 Retrieving base collection data...');
        const url = (
            `/collections/${this.baseCollectionHandle}/products.json?` +
            new URLSearchParams({ limit: 1000 }).toString()
        );
        const collectionData = await fetchData(url);
        this.baseCollectionData = collectionData.products;
        console.log('✅ Base collection retrieved');
    }

    async buildProductData() {
        console.log('🚧 Building base product data...');
        const url = `/products/${this.baseProductHandle}.json`
        const baseProductData = await fetchData(url);
        
        const tags = baseProductData.product.tags.split(', ');
        tags.forEach((tag) => {
            // For each tag...
            if (tag.includes('upsell:')) {
                // Example tag syntax: `upsell:colour:black`
                // If the tag contains the string `upsell:`, we trim and split the string, allowing us to
                // access each part via the index. For each of these tags we find, we add them to the 
                // `this.baseProductData` object for use later.
                let tagSplit = tag.trim().split(":");
                let namespace = tagSplit[1],
                    key = tagSplit[2];
                this.baseProductData[namespace] = key;
            }
        })

        // If product type is a bra and a size property has already been assigned, we remove the 
        // the property as bra size values shouldn't be considered when filtering the base collection
        // by size.
        if (this.baseProductData['product-type'] == 'bra' && this.baseProductData['size']) {
            //to-do: add back in: delete this.baseProductData['size'];
        }

        console.log('✅ Base product data complete.');
    }

    async retrieveOverridesData() {
        let overrideDataArray = []
        for (let i = 0; i < this.upsellOverrideHandles.length; i++) {
            const url = `/products/${this.upsellOverrideHandles[i]}.json`
            const overrideData = await fetchData(url);
            if (overrideData) {
                overrideData.product.overrideProduct = true;
                overrideDataArray.push(overrideData.product);
            }
        }
        return overrideDataArray;
    }

    formatSizeValue(value) {
        // Accepts a product's size variant value, and runs a check to detect whether the
        // current format matches one of the strings in the `acceptedSizeValues` array.
        value = value.toUpperCase();

        // Value is formatted correctly already
        if (this.acceptedSizeValues.includes(value)) { return value };

        // If we detect brackets in the size value, we split the string and retrieve the
        // first part. We then trim the remaining value and check again to see whether the
        // value matches a value in the `acceptedValue` array. We then return the results.
        if (value.includes('(')) {
            value = value.split('(')[0].trim();
            return this.acceptedSizeValues.includes(value) ? value : false;
        }

        return false;
    }

    runFilters() {
        // Big ol' filter function
        console.log(`%c${this.env} upsell instance – Running filters`, 'background: #B4F47D; color: #2E2E2E');

        // Establish how many products we need to return. If 0, we simply return
        // the products from the upsell override metafield.
        const productsRequired = this.productsRequired - this.overridesDataArray.length; // The value with overrides removed.
        let dynamicProductsRequired = productsRequired;
        let preFilterArray = [];
        let postFilterArray = [];
        let productsToReturn = this.overridesDataArray || [];

        if ((this.productsRequired - this.overridesDataArray.length) < 1) {
            console.log('Product overrides array has enough products to return without running filters.');
            this.overridesDataArray.length = this.productsRequired;   
            return;
        }

        // Establish the initial pool of products
        this.filteredProductsArray = this.baseCollectionData;
        console.log('Initial product pool:');
        console.log(this.filteredProductsArray);

        console.log(`Total products required: ${this.productsRequired}`);

        if (this.overridesDataArray.length > 0) {
            console.log(`Product Overrides found: ${this.overridesDataArray.length}`);
            console.log(this.overridesDataArray);
        }

        // Establish the order of filter functions
        const filterFunctions = ['removeBaseProduct', 'removeCartItems', 'removeOverrides', 'gender'].concat(this.settings.priorityOrder);

        // Trigger filter functions, each time capturing a pre-filter state, a post-filter state,
        // ensuring that the size of the postFilterArray is not less than the `productsRequired` value.
        for (let i = 0; i < filterFunctions.length; i++) {
            // For each loop, we capture the pre-filtered state in case
            // the filtered results don't return enough products.

            preFilterArray = this.filteredProductsArray;
            postFilterArray = [];
            let filterMessage;

            switch(filterFunctions[i]) {
                case 'removeBaseProduct':
                    filterMessage = 'Removing base product';
                    postFilterArray = this.removeBaseProduct();
                    break;
                case 'removeCartItems':
                    filterMessage = 'Removing cart items';
                    postFilterArray = this.removeCartItems();
                    break;
                case 'removeOverrides':
                    filterMessage = 'Removing product overrides';
                    postFilterArray = this.removeOverrides();
                    break;
                case 'gender':
                    filterMessage = 'Filtering by gender';
                    postFilterArray = this.filterByGender();
                    break;
                case 'price':
                    filterMessage = 'Filtering by price';
                    postFilterArray = this.filterByPrice();
                    break;
                case 'type':
                    filterMessage = 'Filtering by type';
                    postFilterArray = this.filterByType();
                    break;
                case 'size':
                    filterMessage = 'Filtering by size';
                    postFilterArray = this.filterBySize();
                    break;
                case 'colour':
                    filterMessage = 'Filtering by colour';
                    postFilterArray = this.filterByColour();
                    break;
            }

            // In the event that the postFilter results exactly match the amount of products
            // required, we update the dynamicProductsRequired, save the returned products to
            // the productsToReturn array and break the filter loop.
            if (postFilterArray.length == dynamicProductsRequired) {
                console.log('✅', filterMessage, '– Filter returned the exact amount of required products. Filtering finished.')
                dynamicProductsRequired = 0;
                productsToReturn = productsToReturn.concat(...postFilterArray);
                break;
            }

            // If there are no returned products to save...
            // restore the filteredProducts to the preFilter state and continue.
            if (postFilterArray.length <= 0) {
                this.filteredProductsArray = preFilterArray;
                console.log('🚨', filterMessage, `– Filter returned zero products. Resetting product pool to its previous state and continuing...`,'\n','\n',`New product pool size: ${this.filteredProductsArray.length}`,'\n','\n',`Stored products: ${productsToReturn.length} / ${this.productsRequired}`)

                // Then restart filters (if available)
                continue;
            }

            // In the more likely event that a filter function returns results but with a smaller 
            // product count that is required...
            if (postFilterArray.length > 0 && postFilterArray.length < dynamicProductsRequired) {
                // If so, saved the returned products to the productsToReturn array{}
                productsToReturn = productsToReturn.concat(...postFilterArray);

                // And update the dynamicProductsRequired value
                dynamicProductsRequired = dynamicProductsRequired - postFilterArray.length;

                // Restore the filteredProducts to the preFilter state, but remove the saved
                // products so they're not filtered out in the remaining loops.
                this.filteredProductsArray = preFilterArray.filter((item) => {
                    return !productsToReturn.includes(item);
                });

                console.log('🚨', filterMessage, `– Filter returned too few products. Saving the ${postFilterArray.length} returned products and resetting the product pool to its previous state, minus the saved product(s).`,'\n','\n',`New product pool size: ${this.filteredProductsArray.length}`,'\n','\n',`Stored products: ${productsToReturn.length} / ${this.productsRequired}`);

                // Then restart filters (if available)
                continue;
            }

            // In all other cases, we assume that the filter was successful and the returned product pool
            // was greater than the dynamicRequiredProducts value.
            console.log('✅', filterMessage, '– Complete','\n','\n',`Products filtered during this loop: ${preFilterArray.length - postFilterArray.length}`,'\n',`New product pool size: ${postFilterArray.length}`,'\n','\n',`Stored products: ${productsToReturn.length} / ${this.productsRequired}`)
        }

        // End of filter loop 
        console.log(`%c${this.env} upsell instance – Filters finished`, 'background: #B4F47D; color: #2E2E2E');
        console.log(`Final number of stored products: ${productsToReturn.length} / ${this.productsRequired}`);

        if (productsToReturn.length == this.productsRequired) {
            console.log('✅ Correct amount of products stored. Returning products.');
            return productsToReturn;
        }

        if (productsToReturn.length < this.productsRequired) {
            console.log('✅ Adding final products to the stored product array and returning...');
            this.filteredProductsArray.length = dynamicProductsRequired
            return productsToReturn.concat(...this.filteredProductsArray);
        }

        // Fetch rich product data for the remaining product IDs
        console.log('❌ Something\'s gone wrong...');
        this.cartDrawerAnimate(false);
        return false;
    }

    removeBaseProduct() {
        this.filteredProductsArray = this.filteredProductsArray.filter(item => item.id != this.baseProductId);
        return this.filteredProductsArray;
    }

    removeCartItems() {
        this.cartState.items.forEach((cartItem) => {
            this.filteredProductsArray = this.filteredProductsArray.filter(item => item.id != cartItem.product_id);
        })
        return this.filteredProductsArray;
    }
    
    removeOverrides() {
        if (this.upsellOverrideHandles) {
            this.upsellOverrideHandles.forEach((override) => {
                this.filteredProductsArray = this.filteredProductsArray.filter(item => item.handle != override);
            })
        }
        return this.filteredProductsArray;
    }

    filterByGender() {
        if (this.baseProductData['product-gender'] == 'men' || this.baseProductData['product-gender'] == 'women') {
            let baseGenderTag = `upsell:product-gender:${this.baseProductData['product-gender']}`;
            this.filteredProductsArray = this.filteredProductsArray.filter((item) => {
                if (item.tags.includes(baseGenderTag) || item.tags.includes('upsell:product-gender:unisex')) { return true };
            });
        }
        return this.filteredProductsArray;
    }

    filterByPrice() {
        // Settings
        const promoteFreeDelivery = this.settings.blockSettings.priceLimitsSettings.freeDelivery;
        const freeDeliveryThreshold = this.settings.blockSettings.priceLimitsSettings.freeDeliveryThreshold;
        const freeDeliveryThresholdValue = this.settings.blockSettings.priceLimitsSettings.freeDeliveryThresholdValue * 100;
        const freeDeliveryAmount = this.settings.blockSettings.priceLimitsSettings.freeDeliveryAmount * 100;

        // The distance that the user is away from free delivery
        const distanceFromFreeDelivery = freeDeliveryAmount - this.cartState.total_price;
        
        if (promoteFreeDelivery && freeDeliveryAmount > 0) {
            // Calculate free delivery logic
            // Work out if the user's cart total is within the threshold to promote products based on free delivery.
            // If it's not, we return without filtering.);
            if (freeDeliveryThreshold && freeDeliveryThresholdValue > 0 && distanceFromFreeDelivery > freeDeliveryThresholdValue) {
                console.log('🚧 Cart total not close enough to Free Shipping threshold in order to promote Free Delivery items. To update this setting, navigate to the Global Product Upsell section settings > Price Limits block > Free delivery logic threshold (£).');
                return this.filteredProductsArray;
            }

            this.filteredProductsArray = this.filteredProductsArray.filter((item) => {
                // For each item in the product pool, we calculate its priceFrom value.
                let priceFrom = [];
                item.variants.forEach((variant) => {
                    if (variant.available) priceFrom.push(Math.round(variant.price * 100));
                })
                priceFrom.length > 0 ? priceFrom = Math.min(...priceFrom) : priceFrom = 0;
                // If the product's priceFrom value is less than the amount required to hit the
                // free delivery mark, we turn false.
                if (priceFrom > distanceFromFreeDelivery) { return true };
            });
        }

        return this.filteredProductsArray;
    }

    filterBySize() {
        // Settings
        const sizeDifference = this.settings.blockSettings.sizeSettings.sizeDifference;

        // If base product has a defined size, and is not a bra...
        if (this.baseProductData['size'] && this.baseProductData['product-type'] != 'bra') {

            // Calculate where the base product size lives within the array of accepted values
            const baseProductSizeIndex = this.acceptedSizeValues.indexOf(this.baseProductData.size);

            // Add the base product's size into the array
            let filterSizesArray = [this.baseProductData.size];

            // For each size difference that is defined the setting, we will attempt retrieve the 
            // size from either side of the base product size's position.
            for (let i = 0; i < sizeDifference; i++) {
                let index1 = i + 1;
                if (this.acceptedSizeValues[baseProductSizeIndex + index1]) {
                    filterSizesArray.push(this.acceptedSizeValues[baseProductSizeIndex + index1]);
                }
                if (this.acceptedSizeValues[baseProductSizeIndex - index1]) {
                    filterSizesArray.push(this.acceptedSizeValues[baseProductSizeIndex - index1]);
                }
            }

            this.filteredProductsArray = this.filteredProductsArray.filter((item) => {
                // Ensure that all bras are not excluded from the filter, as we don't corelate 
                // bra size to other item sizes (i.e. base-layer, underwear, shorts, etc.).
                if (item.tags.includes('upsell:product-type:bra')) {
                    return true;
                }

                // Check each product's variants, looking for an instance of a in stock size
                // that exists in the `filterSizesArray` array.
                for (let i = 0; i < item.variants.length; i++) {

                    // Save the variant to a variable and check its availability – skip this loop if not available.
                    let variant = item.variants[i];
                    if (!variant.available) { continue };

                    // Array of the variant options we can check
                    let options = [variant.option1, variant.option2, variant.option3];
                    
                    for (let i = 0; i < options.length; i++) {
                        // Check the option has a value...
                        if (options[i]) {
                            // Check that the value returns a formatted size value when ran through formatSizeValue().
                            let formattedVariantSize = this.formatSizeValue(options[i])

                            // If it does return true, and the formatted size is in our `filterSizesArray` array, we 
                            // return true to add this product back into the product pool.
                            if (formattedVariantSize && filterSizesArray.includes(formattedVariantSize)) {
                                return true;
                            }
                        }
                    }
                }
            })
        }
        return this.filteredProductsArray;
    }

    filterByType() {
        // Settings
        const productTypeAssociation = this.settings.blockSettings.productTypeSettings.productTypeAssociation;
        
        if (this.baseProductData['product-type'] && productTypeAssociation[this.baseProductData['product-type']]) {
            // Retrieve the relating product-types for the baseProduct's type. We then run a trim on each one to tidy things up.
            let associatedTypesTags = productTypeAssociation[this.baseProductData['product-type']].split(',');
            associatedTypesTags = associatedTypesTags.map(e => {
                return `upsell:product-type:${e.trim()}`;
            });

            this.filteredProductsArray = this.filteredProductsArray.filter((item) => {
                if (item.tags.filter(tag => associatedTypesTags.includes(tag)).length > 0) { return true };
            });
        }

        return this.filteredProductsArray;
    }

    filterByColour() {
        if (this.baseProductData['colour']) {
            let baseColourTag = `upsell:colour:${this.baseProductData['colour']}`;
            this.filteredProductsArray = this.filteredProductsArray.filter((item) => {
                if (item.tags.includes(baseColourTag)) { return true };
            });
        }
        return this.filteredProductsArray;
    }

    async retrieveRichData(results) {
        for (let i = 0; i < results.length; i++) {
            // Check if product is an override product, if it, do nothing.
            if (results[i].overrideProduct) { continue };

            const url = `/products/${results[i].handle}.json`
            const productData = await fetchData(url);
            if (productData) {
                // Update product data with rich data.
                results[i] = productData.product;
            }
        }

        console.log('🚧 Updating product data to ensure all data is consistent...');
        return results;
    }

    async createUpsellElement() {

        console.log('🚧 Attempting to create Upsell Element...');

        // First we retrieve the markup for each of the filtered children, and add
        // each instance into the `productsMarkupString` string.
        let productsMarkupString = '';
        for (let i = 0; i < this.finalProducts.length; i++) {
            const url = (
                `/products/${this.finalProducts[i].handle}?` +
                new URLSearchParams({ view: "scroll-carousel-skeleton-cart-drawer" }).toString()
            );
            const productElement = await fetchData(url);

            if (productElement) {
                productsMarkupString = productsMarkupString.concat(productElement)
            };
        }

        // We then parse them and retrieve the innerHTML
        let productsDataParse = new DOMParser()
            .parseFromString(productsMarkupString, "text/html")
            .querySelector('body').innerHTML;

        // Next we retrieve the section template using the Section Rendering API...
        const sectionName = `scroll-carousel-skeleton-cart-drawer`
        const url = (
            `/?` +
            new URLSearchParams({ sections: sectionName }).toString()
        );
        const sectionData = await fetchData(url);
        if (!sectionData) {
            console.log('❌  Could not retrieve Upsell Element markup');
            this.cartDrawerAnimate(false);
            return;
        };

        // Parse section markup, retrieving the innerHTML
        let sectionDataParse = new DOMParser()
            .parseFromString(sectionData[sectionName], "text/html")
            .querySelector(`#shopify-section-scroll-carousel-skeleton-cart-drawer`).innerHTML;

        // Parse section's inner HTML markup so we can run a query selector...
        sectionDataParse = new DOMParser().parseFromString(sectionDataParse, 'text/html');

        // Run query selector to identify the location where our products will sit, and then
        // update the innerHTML of the location and updated with our newley parsed product data.
        sectionDataParse.querySelector('[data-upsell-content="products"]').innerHTML = productsDataParse;

        // `sectionDataParse` now represents a document node, and so the final step is to retrieve the 
        // inner element we require by using another query selector.
        const element = sectionDataParse.querySelector('.cart-drawer__recommendations');
        
        console.log('🚧 Inserting upsell element...');

        // Work out where we'll inject the upsell – Cart Drawer works slightly differently due to
        // it not existing on page load when the cart.items == 0.
        this.insertLocation = this;
        if (this.env == 'CartDrawer') {
            this.insertLocation = document.querySelector('[data-cart-drawer-upsell-location]');
        }

        if (!this.insertLocation) {
            console.log('❌ Issue with insert location.');
            this.cartDrawerAnimate(false);
            return;
        }

        // Clear the insertLocation ready for a new instance
        this.insertLocation.innerHTML = "";
        this.insertLocation.style.position = 'static';
        this.insertLocation.append(element);
        
        // Update header text
        let header = element.querySelector('[data-upsell-content="header"]');
        let headerText = this.settings.accompanyingText.cartDrawer || 'Recommended for you...';
        if (this.inBundle && this.currentBundleData) {
            headerText = this.currentBundleData.titleOverride || this.currentBundleData.discountName;
        }
        header.innerText = headerText;

        // Reveal element
        element.removeAttribute('hidden');
        this.cartDrawerRecommendations.setAttribute('hidden', true);

        console.log('✅ All done!');

        // If a user adds a product to the cart, from the cart drawer, then the variant selector 
        // modal can hide new upsell products. The below checks if the back-to-cart button is 
        // retrievable, and if it is, clicks it to ensure the user can see the new upsells.
        // this.closeVariantModal = document.querySelector('button[data-product-upsell-back-to-cart]');
        // if (this.closeVariantModal) { (this.closeVariantModal).click() };

        // Scroll into view
        this.insertLocation.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
    }

    cartDrawerAnimate(animate) {
        this.cartDrawerRecommendations = document.querySelector('.cart-drawer__recommendations');
        if (this.cartDrawerRecommendations && animate) {
            this.cartDrawerRecommendations.classList.add('product-upsell-loading-state');
            // Remove animation if the class still remains in 10 seconds (incase something goes wrong!)
            window.setTimeout(() => {
                this.cartDrawerRecommendations?.classList.remove('product-upsell-loading-state')
            }, 10000);

        } else if (this.cartDrawerRecommendations) {
            this.cartDrawerRecommendations.classList.remove('product-upsell-loading-state');
        }
    }
};

if (!window.customElements.get("global-product-upsell")) {
    window.customElements.define("global-product-upsell", GlobalProductUpsell);
}

var CollapsibleVariants = class extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[data-toggle-variant-collapse]').addEventListener('click', this.showHide.bind(this));
  }

  showHide() {
    this.querySelector('[data-collapsible-container]').classList.toggle('collapsed')
  }
};

if (!window.customElements.get("collapsible-variants")) {
  window.customElements.define("collapsible-variants", CollapsibleVariants);
}

export {
    CollapsibleVariants,
    GlobalProductUpsell
}